import Head from 'next/head';
import { ArticleJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useTranslation } from 'lib/hooks/useTranslation';
import { Storyblok } from 'types';

type SeoProps = {
  metadata: Storyblok.Metadata & {
    linkCanonical?: string;
  };
  noindex: boolean;
  nofollow: boolean;
  linkDefaultPage?: string;
  linkCanonicalPage?: string;
  linkDefaultCategory?: string;
  linkCanonicalCategory?: string;
  supportedLocales?: string[];
  structuredData?: Array<{
    type: 'article' | 'faq';
    data: any & {
      linkDefault?: string;
      linkCanonical?: string;
    };
  }>;
};

export default function Seo({ metadata, noindex, nofollow, linkDefaultPage, linkCanonicalPage, linkDefaultCategory, linkCanonicalCategory, supportedLocales, structuredData, }: SeoProps) {
  const { asPath } = useRouter();
  const { defaultLocale, locale, locales } = useTranslation();

  const structuredDataArticle = structuredData?.find((e) => e.type === 'article');
  const structuredDataFaq = structuredData?.find((e) => e.type === 'faq');

  const localesList = supportedLocales ?? locales;

  const defaultLang =
    linkDefaultCategory ||
    (structuredDataArticle ? structuredDataArticle.data.linkDefault : undefined) ||
    linkDefaultPage ||
    defaultLocale;

  const canonicalLang =
    linkCanonicalCategory ||
    (structuredDataArticle ? structuredDataArticle.data.linkCanonical : undefined) ||
    linkCanonicalPage ||
    metadata.linkCanonical ||
    defaultLocale;

  const hrefLangMap: Record<string, string> = {
    en: 'en',
    'en-gb': 'en-gb',
    fr: 'fr',
  };

  const buildUrl = (lang: string): string => {
    const base = `https://upflow.io${lang === defaultLocale ? asPath : `/${lang}${asPath}`}`;
    const u = new URL(base);
    u.search = '';
    u.hash = '';
    return u.toString();
  };

  const defaultUrl = buildUrl(defaultLang);
  const canonicalUrl = buildUrl(canonicalLang);

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />

        {/* x-default */}
        <link rel="alternate" hrefLang="x-default" href={defaultUrl} />

        {/* hreflang for each locale used */}
        {localesList.map((l) => (
          <link
            key={l}
            rel="alternate"
            hrefLang={hrefLangMap[l] || l}
            href={buildUrl(l)}
          />
        ))}
        <meta name="facebook-domain-verification" content="qf56qutzltzxhm80iegxdwv8gkp9nr" />
      </Head>
      <NextSeo
        noindex={noindex}
        nofollow={nofollow}
        title={metadata.title}
        description={metadata.description}
        canonical={canonicalUrl}
        openGraph={{
          site_name: metadata.title,
          type: 'website',
          url: `https://upflow.io${asPath}`,
          title: metadata.og_title,
          description: metadata.og_description,
          images: [
            {
              url: metadata.og_image ? metadata.og_image : 'https://upflow.io/meta/en@2x.png',
              width: 2048,
              height: 1170,
              alt: 'Default OG Image',
            },
          ],
        }}
      />

      {structuredDataArticle && (
        <ArticleJsonLd
          type="BlogPosting"
          url={canonicalUrl}
          title={metadata.title}
          description={metadata.description}
          publisherName="Upflow"
          publisherLogo="https://upflow.io/assets/logo-default.svg"
          {...structuredDataArticle.data}
        />
      )}

      {structuredDataFaq && <FAQPageJsonLd {...structuredDataFaq.data} />}
    </>
  );
}

/**
 * Not used anymore, kept temporary
 */
function stripQueryStringAndHashFromPath(inputUrl: string): string {
  const url = new URL(inputUrl);
  url.search = '';
  url.hash = '';
  return url.toString();
}